import d from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DayCard from './day-card';
import Spinner from './spinner';
import {
  DATE_KEY,
  isPastKey,
  normalizeRange,
  todayKey
} from 'src/utils/calendar-range';
import { hasLocalMonths } from 'src/utils/calendar-info';

const WEEK_NAMES_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEK_NAMES_LONG = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY'
];

/**
 * Multi-month calendar with Booking.com style range selection:
 *  - click a date to select it (start = end)
 *  - click another date (or click-and-drag) to extend the selection into a range
 *  - clicking again after a range is complete starts a new selection
 */
function BookingCalendar({
  monthsData,
  entriesByKey,
  isFetching,
  error,
  range,
  onRangeChange,
  daysOfWeek,
  updatingCalendar,
  room,
  property,
  isMobile
}) {
  const dragRef = useRef(null); // { anchor, moved }
  const [pendingEnd, setPendingEnd] = useState(false);
  const [hoverKey, setHoverKey] = useState(null);

  const commitRange = useCallback(
    (a, b) => {
      const r = normalizeRange(a, b);
      onRangeChange({
        start: isPastKey(r.start) ? todayKey() : r.start,
        end: r.end
      });
    },
    [onRangeChange]
  );

  const handleMouseDown = useCallback(
    key => {
      if (isPastKey(key)) return;
      if (pendingEnd && range?.start && key !== range.start) {
        commitRange(range.start, key);
        setPendingEnd(false);
        setHoverKey(null);
        return;
      }
      dragRef.current = { anchor: key, moved: false };
      setPendingEnd(false);
      setHoverKey(null);
      onRangeChange({ start: key, end: key });
    },
    [pendingEnd, range, commitRange, onRangeChange]
  );

  const handleMouseEnter = useCallback(
    key => {
      if (dragRef.current) {
        if (key !== dragRef.current.anchor) dragRef.current.moved = true;
        commitRange(dragRef.current.anchor, key);
      } else if (pendingEnd) {
        setHoverKey(key);
      }
    },
    [pendingEnd, commitRange]
  );

  useEffect(() => {
    const finishDrag = () => {
      if (!dragRef.current) return;
      const { moved } = dragRef.current;
      dragRef.current = null;
      if (!moved) setPendingEnd(true);
    };
    document.addEventListener('mouseup', finishDrag);
    return () => document.removeEventListener('mouseup', finishDrag);
  }, []);

  // A complete range set from outside (date pickers) ends the "pick an end date" step.
  useEffect(() => {
    if (range?.start && range?.end && range.start !== range.end) {
      setPendingEnd(false);
      setHoverKey(null);
    }
  }, [range?.start, range?.end]);

  const previewRange = useMemo(() => {
    if (!pendingEnd || !hoverKey || !range?.start || hoverKey === range.start)
      return null;
    return normalizeRange(range.start, hoverKey);
  }, [pendingEnd, hoverKey, range?.start]);

  const inRange = key =>
    !!range?.start && key >= range.start && key <= (range.end || range.start);
  const inPreview = key =>
    !!previewRange && key >= previewRange.start && key <= previewRange.end;

  const style = {
    calendarContainer: {
      // On desktop the page wraps this in a relative box that stretches to the
      // side panel's height, so fill it absolutely and scroll inside.
      position: isMobile ? 'relative' : 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: isMobile ? 'auto' : '100%',
      maxHeight: isMobile ? '70vh' : undefined,
      marginBottom: isMobile ? '20px' : '0',
      width: '100%',
      maxWidth: '100%',
      overflow: 'auto',
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
      background: '#fafafa'
    },
    monthContainer: {
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      background: '#fff',
      margin: isMobile ? '10px' : '15px'
    },
    monthHeader: {
      background: '#f9f9f9',
      padding: isMobile ? '12px 16px' : '16px 24px',
      borderBottom: '2px solid #eee',
      textAlign: 'center'
    },
    monthTitle: {
      margin: 0,
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: 700,
      color: '#333'
    },
    weekNames: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      textAlign: 'center',
      borderBottom: '1px solid #eee',
      background: '#f9f9f9',
      padding: isMobile ? '8px 0' : '10px 0'
    },
    daysGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: isMobile ? '4px' : '6px',
      background: '#fff',
      padding: isMobile ? '6px' : '12px'
    },
    overlay: {
      position: 'fixed',
      background: 'rgba(29, 29, 29, 0.5)',
      height: '100vh',
      width: '100vw',
      top: 0,
      left: 0,
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    weekDay: {
      margin: 0,
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: 600,
      color: '#555'
    },
    hintBar: {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: '#f0f8ff',
      padding: '8px 15px',
      fontSize: isMobile ? '12px' : '13px',
      color: '#333',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px'
    }
  };

  return (
    <div
      className="__calendar-container"
      style={style.calendarContainer}
      onMouseLeave={() => setHoverKey(null)}
    >
      {updatingCalendar && (
        <div style={style.overlay}>
          <Spinner />
        </div>
      )}

      <div style={style.hintBar}>
        <span>
          {pendingEnd
            ? 'Now click an end date to select a range, or click the same date again to keep a single day.'
            : 'Click a date, then another date (or click and drag) to select a range across months.'}
        </span>
        {isFetching && (
          <span style={{ color: '#888', whiteSpace: 'nowrap' }}>Loading…</span>
        )}
        {error && (
          <span style={{ color: 'red', whiteSpace: 'nowrap' }}>
            Failed to load
          </span>
        )}
      </div>

      {monthsData.map(monthData => (
        <div
          key={`${monthData.year}-${monthData.month}`}
          className="__calendar-month"
          style={style.monthContainer}
        >
          <div style={style.monthHeader}>
            <h2
              style={style.monthTitle}
              className={hasLocalMonths(monthData.lang) ? 'notranslate' : undefined}
            >
              {monthData.monthName}
            </h2>
          </div>

          <div style={style.weekNames}>
            {(isMobile ? WEEK_NAMES_SHORT : WEEK_NAMES_LONG).map(day => (
              <h3 key={day} style={style.weekDay}>
                {day}
              </h3>
            ))}
          </div>

          <div className="__calendar-days" style={style.daysGrid}>
            {[...Array(monthData.from.day())].map((_, i) => (
              <div key={`empty-${monthData.month}-${i}`} />
            ))}

            {[...Array(monthData.daysInMonth)].map((_, i) => {
              const day = i + 1;
              const key = monthData.from.date(day).format(DATE_KEY);
              const selected = inRange(key);
              return (
                <DayCard
                  key={key}
                  dateKey={key}
                  dayNumber={day}
                  entry={entriesByKey[key]}
                  property={property}
                  room={room}
                  isPast={isPastKey(key)}
                  isMobile={isMobile}
                  inRange={selected}
                  isStart={selected && key === range.start}
                  isEnd={selected && key === (range.end || range.start)}
                  excluded={selected && !daysOfWeek.includes(d(key).day())}
                  preview={!selected && inPreview(key)}
                  onMouseDown={handleMouseDown}
                  onMouseEnter={handleMouseEnter}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default BookingCalendar;
